/**
 * PreviewToolbarControls — pin toggle + status chip when preview is unpinned.
 */

import { FC, useMemo, type MouseEvent, type PointerEvent } from 'react';
import { cn } from '@/lib/utils';

import { useFlowOptimizerLivePreview } from '../../../context/FlowOptimizerLivePreviewContext';
import PreviewLiveBadge from '../PreviewLiveBadge';
import { PreviewPinButton } from './pin/PreviewPinButton';
import {
  previewChipLabel,
  summarizePreviewItems,
} from '../../../utils/preview-summary.util';

export interface PreviewToolbarControlsProps {
  pinned: boolean;
  onTogglePin: () => void;
}

const stopTabBubble = (e: PointerEvent | MouseEvent) => {
  e.stopPropagation();
};

const PreviewToolbarControls: FC<PreviewToolbarControlsProps> = ({
  pinned,
  onTogglePin,
}) => {
  const { live, result } = useFlowOptimizerLivePreview();
  const chip = useMemo(
    () => (result ? previewChipLabel(summarizePreviewItems(result.items)) : null),
    [result],
  );

  return (
    <div
      className="flex shrink-0 items-center gap-1.5 border-l border-border/50 pl-2"
      onPointerDown={stopTabBubble}
      onClick={stopTabBubble}
    >
      {!pinned ? (
        <button
          type="button"
          onClick={onTogglePin}
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md border border-border/60',
            'bg-background/80 px-2 text-[10px] text-muted-foreground hover:bg-muted/40',
          )}
        >
          <PreviewLiveBadge
            isFetching={live.isFetching}
            isStale={live.isStale}
            compact
          />
          <span className="tabular-nums">{chip ?? 'Show panel'}</span>
        </button>
      ) : null}
      <PreviewPinButton pinned={pinned} onToggle={onTogglePin} />
    </div>
  );
};

export default PreviewToolbarControls;
